import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { GetClasses } from '@lifeomic/chroma-react/typeUtils';
import { Download, ExternalLink } from '@lifeomic/chromicons';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import FileManagementControls, {
  checkIsFileServiceFile,
  fileServiceUrlToID,
} from '../../../FileManagementControls';
import { permissions } from '../../../../util/abac';
import { Link } from '@lifeomic/chroma-react/components/Link';
import EnforceABAC from '../../../EnforceABAC';
import FhirService from '../../../../services/FhirService';
import useServiceAuth from '../../../../hooks/useServiceAuth';
import { useActiveAccountId } from '../../../../hooks/useActiveAccountId';
import { useNotify } from '../../../../hooks/useNotify';

const contentTypes = {
  audio: 'audio/wave',
};

const fhirService = new FhirService('DocumentReference');

const messages = defineMessages({
  open: {
    id: 'tableLink.open',
    defaultMessage: 'Open',
  },
  download: {
    id: 'tableLink.download',
    defaultMessage: 'Download',
  },
  openError: {
    id: 'tableLink.openError',
    defaultMessage: 'Error opening file',
  },
  downloadError: {
    id: 'tableLink.downloadError',
    defaultMessage: 'Error downloading file',
  },
});

export const useStyles = makeStyles((theme: Theme) => ({
  fileControlsRoot: {
    justifyContent: 'flex-start',
  },
  inlineControls: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  link: {
    paddingLeft: theme.spacing(1),
  },
}));

export type TableLinkClasses = GetClasses<typeof useStyles>;

export interface TableLinkOwnProps {
  url?: string;
  contentType?: string;
  /** Base64 attachment body, for documents stored inline rather than in file-service. */
  data?: string;
  title?: string;
  resourceId?: string;
  /** Only DocumentReference rows may be fetched when the list payload omitted the body. */
  resourceType?: string;
}

export type TableLinkProps = TableLinkOwnProps;

const decodeBase64 = (data: string): Uint8Array => {
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const isTextContentType = (contentType?: string) =>
  Boolean(contentType?.startsWith('text/'));

const filenameFor = (title: string | undefined, contentType?: string) => {
  const trimmed = title?.trim();
  if (trimmed) {
    return trimmed;
  }
  if (contentType === 'text/markdown') {
    return 'document.md';
  }
  return 'document';
};

const blobFor = (
  bytes: Uint8Array,
  contentType: string | undefined,
  open: boolean,
) => {
  const type =
    open && isTextContentType(contentType)
      ? 'text/plain;charset=utf-8'
      : contentType || 'application/octet-stream';
  if (isTextContentType(contentType)) {
    return new Blob([new TextDecoder().decode(bytes)], { type });
  }
  return new Blob([bytes.buffer], { type });
};

const openBytes = (bytes: Uint8Array, contentType?: string) => {
  const url = URL.createObjectURL(blobFor(bytes, contentType, true));
  window.open(url, '_blank', 'noopener');
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

const downloadBytes = (
  bytes: Uint8Array,
  filename: string,
  contentType?: string,
) => {
  const url = URL.createObjectURL(blobFor(bytes, contentType, false));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const TableLink = (props: TableLinkProps) => {
  const formatMessage = useIntl().formatMessage;
  const classes = useStyles(props);
  const auth = useServiceAuth();
  const accountId = useActiveAccountId();
  const [notify] = useNotify();
  const [isLoading, setIsLoading] = React.useState(false);

  const isFileServiceFile = checkIsFileServiceFile(props.url || '');
  const fileServiceFileId = isFileServiceFile
    ? fileServiceUrlToID(props.url || '')
    : null;
  const hasUrl = Boolean(props.url);
  const canFetchDocument =
    props.resourceType === 'DocumentReference' && Boolean(props.resourceId);
  const hasInlineAttachment =
    Boolean(props.data) || (!hasUrl && canFetchDocument);

  const loadBytes = React.useCallback(async () => {
    if (props.data) {
      return {
        bytes: decodeBase64(props.data),
        contentType: props.contentType,
        title: props.title,
      };
    }
    if (!canFetchDocument || !props.resourceId) {
      throw new Error('No inline attachment');
    }
    const response = await fhirService.getById(auth)(
      accountId,
      props.resourceId,
    );
    const attachment = response.data?.content?.[0]?.attachment;
    if (!attachment?.data) {
      throw new Error('No inline attachment');
    }
    return {
      bytes: decodeBase64(attachment.data),
      contentType: attachment.contentType || props.contentType,
      title: attachment.title || props.title,
    };
  }, [
    accountId,
    auth,
    canFetchDocument,
    props.contentType,
    props.data,
    props.resourceId,
    props.title,
  ]);

  const handleOpenInline = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const loaded = await loadBytes();
      openBytes(loaded.bytes, loaded.contentType);
    } catch (error) {
      console.error(error);
      notify(formatMessage(messages.openError), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [formatMessage, loadBytes, notify]);

  const handleDownloadInline = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const loaded = await loadBytes();
      downloadBytes(
        loaded.bytes,
        filenameFor(loaded.title, loaded.contentType),
        loaded.contentType,
      );
    } catch (error) {
      console.error(error);
      notify(formatMessage(messages.downloadError), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [formatMessage, loadBytes, notify]);

  if (isFileServiceFile) {
    return (
      <EnforceABAC permission={permissions.downloadFile}>
        <FileManagementControls
          className={classes.fileControlsRoot}
          enableRequestDownloadLink
          enableOpen
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          fileId={fileServiceFileId}
          contentType={props.contentType}
        />
      </EnforceABAC>
    );
  } else if (props.url && props.contentType === contentTypes.audio) {
    // eslint-disable-next-line jsx-a11y/media-has-caption
    return <audio src={props.url} controls />;
  } else if (hasInlineAttachment) {
    return (
      <div className={classes.inlineControls}>
        <IconButton
          aria-label={formatMessage(messages.open)}
          icon={ExternalLink}
          onClick={handleOpenInline}
          size={0}
          disabled={isLoading}
        />
        <IconButton
          aria-label={formatMessage(messages.download)}
          icon={Download}
          onClick={handleDownloadInline}
          size={0}
          disabled={isLoading}
        />
      </div>
    );
  } else if (props.url) {
    return (
      <Link to={props.url} className={classes.link} newTab>
        {formatMessage(messages.open)}
      </Link>
    );
  }
  return null;
};

export default React.memo(TableLink);
